import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";

export type AgentGovernanceStatus = "APPROVED" | "BLOCKED" | "PENDING";

export type DashboardDuration = "7" | "30" | "90" | "custom";

export interface DashboardQueryOptions {
    days?: number;
    startDate?: string;
    endDate?: string;
    status?: string;
    search?: string;
    capability?: string;
    limit?: number;
    offset?: number;
}

export interface AgenticActivityOverview {
    totalEvents: number;
    totalAgentsDetected: number;
    approvedAgents: number;
    blockedAgents: number;
    totalEndpointsWithAi: number;
    activeUsers: number;
}

export interface AgenticActivityTimelinePoint {
    date: string;
    events: number;
}

export interface AgentInventoryItem {
    agentId: number;
    agentKey: string;
    displayName: string;
    status: AgentGovernanceStatus;
    events: number;
    endpoints: number;
    users: number;
    firstSeen: string | null;
    lastSeen: string | null;
    activityShare: number;
    capabilities: string[];
}

export interface AgentEndpointDetail {
    computerId: number;
    computerName: string;
    osName: string;
    events: number;
    lastSeen: string | null;
    otherAgents: string[];
    users: string[];
}

export interface AgentTopUser {
    userId: number;
    username: string;
    events: number;
    lastSeen: string | null;
}

export interface AgentDetailsResponse {
    agentDetails: AgentInventoryItem;
    endpoints: AgentEndpointDetail[];
    topUsers: AgentTopUser[];
}

export interface PagedAgentEndpointsResponse {
    items: AgentEndpointDetail[];
    total: number;
    hasMore: boolean;
}

export interface TopEndpointItem {
    computerId: number;
    computerName: string;
    osName?: string;
    events: number;
    lastSeen: string | null;
    agents: string[];
}

export interface TopUserItem {
    userId: number;
    username: string;
    events: number;
    lastSeen: string | null;
    agents: string[];
    endpoints: string[];
}

export class AgenticActivityApiError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AgenticActivityApiError";
    }
}

export const CAPABILITY_FILTER_OPTIONS = [
    { value: "", label: "Any system access" },
    { value: "FILE_SYSTEM", label: "File System" },
    { value: "NETWORK", label: "Network" },
    { value: "SHELL", label: "Shell" },
    { value: "CLIPBOARD", label: "Clipboard" },
    { value: "BROWSER", label: "Browser" },
] as const;

function buildTenantQuery(tenantId: string, extra?: Record<string, string | number | undefined>): string {
    const params = new URLSearchParams();
    params.set("tenantId", tenantId);
    params.set("tenantName", tenantId);
    if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
            if (value != null && value !== "") {
                params.set(key, String(value));
            }
        });
    }
    return params.toString();
}

function resolveTenantId(tenantId?: string | null): string {
    if (tenantId?.trim()) {
        return tenantId.trim();
    }
    throw new AgenticActivityApiError("tenantId is required");
}

export function buildDashboardRangeParams(
    duration: DashboardDuration,
    customStartDate?: string,
    customEndDate?: string
): Pick<DashboardQueryOptions, "days" | "startDate" | "endDate"> {
    if (duration === "custom") {
        return {
            startDate: customStartDate,
            endDate: customEndDate,
        };
    }
    return { days: Number(duration) };
}

export const getAgenticActivityOverview = (tenantId: string, options: DashboardQueryOptions = {}) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<AgenticActivityOverview>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/overview?${buildTenantQuery(tenant, options as Record<string, string | number>)}`,
    });
};

export const getAgenticActivityTimeline = (tenantId: string, options: DashboardQueryOptions = {}) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<AgenticActivityTimelinePoint[]>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/timeline?${buildTenantQuery(tenant, options as Record<string, string | number>)}`,
    });
};

export const getAgentInventory = (tenantId: string, options: DashboardQueryOptions = {}) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<AgentInventoryItem[]>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/agents?${buildTenantQuery(tenant, options as Record<string, string | number>)}`,
    });
};

export const getAgentDetails = (tenantId: string, agentId: number) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<AgentDetailsResponse>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/agents/${agentId}?${buildTenantQuery(tenant)}`,
    });
};

export const getAgentEndpoints = (
    tenantId: string,
    agentId: number,
    options: DashboardQueryOptions = {}
) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<PagedAgentEndpointsResponse>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/agents/${agentId}/endpoints?${buildTenantQuery(tenant, options as Record<string, string | number>)}`,
    });
};

export const getTopEndpoints = (tenantId: string, options: DashboardQueryOptions = {}) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<TopEndpointItem[]>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/top-endpoints?${buildTenantQuery(tenant, { limit: 10, ...options } as Record<string, string | number>)}`,
    });
};

export const getTopUsers = (tenantId: string, options: DashboardQueryOptions = {}) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<TopUserItem[]>({
        method: "GET",
        url: `${NGROK}/api/agentic-activity/top-users?${buildTenantQuery(tenant, { limit: 10, ...options } as Record<string, string | number>)}`,
    });
};

export const updateAgentStatus = (
    tenantId: string,
    agentId: number,
    status: AgentGovernanceStatus
) => {
    const tenant = resolveTenantId(tenantId);
    return apiClient<AgentInventoryItem>({
        method: "PUT",
        url: `${NGROK}/api/agentic-activity/agents/${agentId}/status?${buildTenantQuery(tenant)}`,
        data: { status },
    });
};
